import React from 'react';
import {
  User,
  FileText,
  Tag,
  AlertTriangle,
  CheckCircle,
  Clock,
  MessageSquare,
  ArrowUp,
  Shield,
  Edit,
  Plus,
  Minus,
  Eye,
} from 'lucide-react';

const styles = {
  timeline: {
    position: 'relative',
    paddingLeft: '2rem',
  },
  timelineLine: {
    position: 'absolute',
    left: '0.75rem',
    top: '0.5rem',
    bottom: '0.5rem',
    width: '2px',
    background: 'var(--color-border)',
  },
  event: {
    position: 'relative',
    paddingBottom: '1.5rem',
  },
  eventLast: {
    paddingBottom: 0,
  },
  eventIcon: {
    position: 'absolute',
    left: '-2rem',
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--color-background)',
    border: '2px solid var(--color-border)',
  },
  eventIconCreated: {
    borderColor: 'var(--color-primary)',
    color: 'var(--color-primary)',
  },
  eventIconAssigned: {
    borderColor: 'var(--color-info)',
    color: 'var(--color-info)',
  },
  eventIconNote: {
    borderColor: 'var(--color-text-secondary)',
    color: 'var(--color-text-secondary)',
  },
  eventIconEscalated: {
    borderColor: 'var(--color-warning)',
    color: 'var(--color-warning)',
  },
  eventIconResolved: {
    borderColor: 'var(--color-success)',
    color: 'var(--color-success)',
  },
  eventIconTag: {
    borderColor: 'var(--color-primary)',
    color: 'var(--color-primary)',
  },
  eventIconScreening: {
    borderColor: 'var(--color-error)',
    color: 'var(--color-error)',
  },
  eventIconDocument: {
    borderColor: 'var(--color-info)',
    color: 'var(--color-info)',
  },
  eventIconViewed: {
    borderColor: 'var(--color-text-muted)',
    color: 'var(--color-text-muted)',
  },
  eventContent: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
  },
  eventHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginBottom: '0.25rem',
  },
  eventTitle: {
    margin: 0,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
  },
  eventTime: {
    fontSize: '0.75rem',
    color: 'var(--color-text-muted)',
    whiteSpace: 'nowrap',
  },
  eventDescription: {
    margin: 0,
    fontSize: '0.8125rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.4,
  },
  eventMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: 'var(--color-text-muted)',
  },
  eventActor: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--color-text-muted)',
  },
  emptyIcon: {
    marginBottom: '0.5rem',
    opacity: 0.5,
  },
};

const EVENT_CONFIG = {
  case_created: {
    icon: Plus,
    iconStyle: 'eventIconCreated',
    getTitle: () => 'Case created',
  },
  case_assigned: {
    icon: User,
    iconStyle: 'eventIconAssigned',
    getTitle: (data) => data?.assignee_name
      ? `Assigned to ${data.assignee_name}`
      : 'Case assigned',
  },
  case_unassigned: {
    icon: User,
    iconStyle: 'eventIconAssigned',
    getTitle: () => 'Case unassigned',
  },
  note_added: {
    icon: MessageSquare,
    iconStyle: 'eventIconNote',
    getTitle: () => 'Note added',
  },
  case_escalated: {
    icon: ArrowUp,
    iconStyle: 'eventIconEscalated',
    getTitle: (data) => data?.escalated_to_name
      ? `Escalated to ${data.escalated_to_name}`
      : 'Case escalated',
  },
  case_resolved: {
    icon: CheckCircle,
    iconStyle: 'eventIconResolved',
    getTitle: (data) => `Resolved as ${data?.resolution || 'completed'}`,
  },
  case_reopened: {
    icon: Clock,
    iconStyle: 'eventIconCreated',
    getTitle: () => 'Case reopened',
  },
  status_changed: {
    icon: Edit,
    iconStyle: 'eventIconNote',
    getTitle: (data) => data?.new_status
      ? `Status changed to ${data.new_status}`
      : 'Status updated',
  },
  priority_changed: {
    icon: AlertTriangle,
    iconStyle: 'eventIconEscalated',
    getTitle: (data) => data?.new_priority
      ? `Priority changed to ${data.new_priority}`
      : 'Priority updated',
  },
  tag_added: {
    icon: Tag,
    iconStyle: 'eventIconTag',
    getTitle: (data) => data?.tag_name
      ? `Tag "${data.tag_name}" added`
      : 'Tag added',
  },
  tag_removed: {
    icon: Minus,
    iconStyle: 'eventIconTag',
    getTitle: (data) => data?.tag_name
      ? `Tag "${data.tag_name}" removed`
      : 'Tag removed',
  },
  screening_hit_reviewed: {
    icon: Shield,
    iconStyle: 'eventIconScreening',
    getTitle: () => 'Screening hit reviewed',
  },
  document_reviewed: {
    icon: FileText,
    iconStyle: 'eventIconDocument',
    getTitle: (data) => data?.document_type
      ? `${data.document_type} reviewed`
      : 'Document reviewed',
  },
  case_viewed: {
    icon: Eye,
    iconStyle: 'eventIconViewed',
    getTitle: () => 'Case viewed',
  },
};

function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function TimelineEvent({ event, isLast }) {
  const config = EVENT_CONFIG[event.event_type] || {
    icon: Clock,
    iconStyle: 'eventIconNote',
    getTitle: () => event.event_type?.replace(/_/g, ' ') || 'Event',
  };

  const Icon = config.icon;
  const title = config.getTitle(event.data || {});

  return (
    <div style={{ ...styles.event, ...(isLast ? styles.eventLast : {}) }}>
      <div style={{ ...styles.eventIcon, ...styles[config.iconStyle] }}>
        <Icon size={12} />
      </div>
      <div style={styles.eventContent}>
        <div style={styles.eventHeader}>
          <h4 style={styles.eventTitle}>{title}</h4>
          <span style={styles.eventTime}>{formatTimeAgo(event.created_at)}</span>
        </div>
        {event.description && (
          <p style={styles.eventDescription}>{event.description}</p>
        )}
        {event.actor_name && (
          <div style={styles.eventMeta}>
            <span style={styles.eventActor}>
              <User size={12} />
              {event.actor_name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CaseTimeline({ events = [] }) {
  if (!events || events.length === 0) {
    return (
      <div style={styles.emptyState}>
        <Clock size={32} style={styles.emptyIcon} />
        <p>No activity yet</p>
      </div>
    );
  }

  return (
    <div style={styles.timeline}>
      <div style={styles.timelineLine} />
      {events.map((event, index) => (
        <TimelineEvent
          key={event.id || index}
          event={event}
          isLast={index === events.length - 1}
        />
      ))}
    </div>
  );
}
